// VOYA9 — "What We Help With" public page (/what-we-help-with).
//
// A pre-auth content/SEO surface that maps member-language concerns to
// specific VOYA9 programs. Spec: VOYA9_What_We_Help_With_Page.md.
//
// Tone is welcoming-to-the-uncertain — member-centered language, not
// medical-treatment language. The "When VOYA9 isn't the answer" and
// "Brand-discipline observations" sections are intentionally prominent
// and counter-promotional; they do the credibility work for the page.
//
// All program links route to the PUBLIC marketing pages
// (/programs/<slug>) — those render via MarketingProgram.jsx and work
// for both active and coming-soon programs.
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { C } from "@/lib/brand";
import { useMarketingSEO } from "@/lib/marketingSEO";

// ---- Concern catalog ----
// Each category renders as an H2 section; entries within each are H3 +
// paragraph + a "Where this goes" link. Keep copy verbatim from the
// spec — voice is doing real work here.
const CATEGORIES = [
  {
    n: "01",
    title: "Weight, appetite, and metabolism",
    concerns: [
      {
        h: "“I want to lose weight but nothing’s working”",
        body: "This is the most common concern members bring to VOYA9. Whether weight has crept up over years or shifted recently, the experience of trying — and feeling like nothing moves the dial — is familiar to many. Our Weight Management program offers physician-supervised access to GLP-1 family compounds (Tirzepatide and Retatrutide), which work on appetite regulation and metabolic pathways your body already uses. Average weight loss in clinical trials ranges from 15–28% depending on the compound and individual response.",
        whereLabel: "Weight Management program",
        to: "/programs/weight-management",
      },
      {
        h: "“My appetite feels out of control”",
        body: "The “food noise” that some members describe — constant background chatter about what to eat next, difficulty stopping when satisfied, eating past comfort — often responds well to GLP-1 family compounds. The mechanism involves the same hormones your gut releases when you’ve eaten enough.",
        whereLabel: "Weight Management program",
        to: "/programs/weight-management",
      },
      {
        h: "“I have prediabetes or insulin resistance”",
        body: "Members with diagnosed prediabetes, insulin resistance, or related metabolic concerns often see meaningful improvements in both weight and metabolic markers with GLP-1 family compounds. Tirzepatide in particular has strong evidence for cardiometabolic improvements alongside weight management.",
        whereLabel: "Weight Management program",
        to: "/programs/weight-management",
      },
      {
        h: "“I have BMI 35+ with weight-related comorbidities”",
        body: "Higher-BMI situations with weight-related conditions (knee osteoarthritis, fatty liver, sleep apnea, prediabetes, cardiovascular risk factors) may benefit from more substantial interventions. Retatrutide, an investigational triple-agonist, has shown the highest weight loss results in clinical trials and includes potential benefits for these comorbidities. Your physician determines fit.",
        whereLabel: "Weight Management program (Retatrutide via physician consultation)",
        to: "/programs/weight-management",
      },
    ],
  },
  {
    n: "02",
    title: "Injury, recovery, and tissue healing",
    concerns: [
      {
        h: "“I have a stubborn injury that won’t heal”",
        body: "Tendons, ligaments, soft tissue injuries that have plateaued under conventional rehabilitation — these are common reasons members seek out our Recovery & Healing program. BPC-157 (the foundational tissue repair compound) addresses these focused recovery needs. For more comprehensive situations, Recovery Blend combines BPC-157 with TB-500 for systemic regeneration.",
        whereLabel: "Recovery & Healing program",
        to: "/programs/recovery-healing",
      },
      {
        h: "“My joints and tendons just don’t recover like they used to”",
        body: "The decline in recovery capacity that comes with age, training stress, or chronic wear is a real biological phenomenon. Our recovery compounds support the same healing systems your body already uses — angiogenesis, fibroblast activity, cellular migration — at amplified levels.",
        whereLabel: "Recovery & Healing program",
        to: "/programs/recovery-healing",
      },
      {
        h: "“I’m recovering from surgery and want to support healing”",
        body: "Post-surgical recovery support requires careful coordination with your surgeon and physician oversight. BPC-157 and Recovery Blend can support tissue healing during the recovery window when started at appropriate timing. Talk to your surgeon and our physician before starting.",
        whereLabel: "Recovery & Healing program (with surgical coordination)",
        to: "/programs/recovery-healing",
      },
      {
        h: "“I have chronic gut issues — IBS, gastritis, intestinal inflammation”",
        body: "BPC-157 was originally identified by researchers studying the remarkable healing capacity of the gastrointestinal tract. It’s used clinically for gut healing protocols — though human research remains more limited than the underlying mechanisms suggest. Your physician determines fit based on your specific situation.",
        whereLabel: "Recovery & Healing program (BPC-157)",
        to: "/programs/recovery-healing",
      },
      {
        h: "“I want to support my skin alongside other recovery work”",
        body: "Skin and connective tissue are part of how your body recovers. GHK-Cu (a copper-binding tripeptide with substantial human research for collagen and dermal support) is part of our Recovery & Healing program. For members combining recovery work with skin firmness goals, GLOW (BPC-157 + TB-500 + GHK-Cu) layers all three mechanisms in a single formulation.",
        whereLabel: "Recovery & Healing program (GHK-Cu, GLOW)",
        to: "/programs/recovery-healing",
      },
      {
        h: "“I’m dealing with chronic inflammation alongside recovery needs”",
        body: "KLOW Multi-Peptide Blend adds KPV (a potent anti-inflammatory peptide) to the GLOW formulation. Four mechanisms in one injection: tissue repair, systemic regeneration, collagen support, and inflammation modulation. For members where inflammation is meaningfully limiting recovery, this is the most comprehensive option.",
        whereLabel: "Recovery & Healing program (KLOW)",
        to: "/programs/recovery-healing",
      },
    ],
  },
  {
    n: "03",
    title: "Sexual function and libido",
    concerns: [
      {
        h: "“My libido isn’t what it used to be”",
        body: "Decreased desire, reduced interest in sexual activity, or a general dimming of sexual engagement — these shifts can have many causes (hormones, mood, medications, relationship dynamics, stress, aging). PT-141 (Bremelanotide) is a peptide that works on the central nervous system pathways involved in sexual arousal and desire, distinct from medications like Viagra that work on blood flow. FDA-approved for hypoactive sexual desire disorder in women, used clinically for both men and women.",
        whereLabel: "Sexual Wellness program (PT-141)",
        to: "/programs/sexual-wellness",
      },
      {
        h: "“My arousal feels different — physical response isn’t connecting”",
        body: "Arousal concerns distinct from physical response (where PDE5 inhibitors like Viagra typically aren’t the answer) may benefit from a centrally-acting approach. PT-141 affects the brain pathways involved in arousal rather than peripheral blood flow.",
        whereLabel: "Sexual Wellness program (PT-141)",
        to: "/programs/sexual-wellness",
      },
      {
        h: "“I’m experiencing sexual side effects from antidepressants or other medications”",
        body: "Medication-induced sexual dysfunction (especially from SSRIs) is common and often distressing. The clinical picture is complex — sometimes the right answer is medication adjustment with your prescribing physician, sometimes it’s adjunctive support like PT-141, sometimes both. This warrants careful evaluation rather than self-direction to a single compound.",
        whereLabel: "Assessment-led consultation ($75) for complex evaluation, possibly Sexual Wellness program after review",
        to: "/get-access",
      },
    ],
  },
  {
    n: "04",
    title: "Aesthetic, skin, and collagen",
    concerns: [
      {
        h: "“My skin doesn’t bounce back like it used to”",
        body: "Skin elasticity, firmness, and resilience decline as natural GHK-Cu levels drop with age (from ~200 ng/ml at 20 to ~80 ng/ml at 60). Supplementing this peptide stimulates collagen production and supports dermal recovery. Note: we organize this under Recovery & Healing rather than a separate “Skin” program because the clinical mechanism is tissue regeneration — same biology as our recovery work, just expressed as skin appearance.",
        whereLabel: "Recovery & Healing program (GHK-Cu)",
        to: "/programs/recovery-healing",
      },
      {
        h: "“I want to recover faster from cosmetic procedures”",
        body: "GHK-Cu accelerates epithelial recovery after laser resurfacing, microneedling, and chemical peels in published clinical research. Members coordinating cosmetic procedures with peptide protocols often find the recovery window meaningfully shortened.",
        whereLabel: "Recovery & Healing program (GHK-Cu) — coordinate timing with your aesthetic provider",
        to: "/programs/recovery-healing",
      },
      {
        h: "“Comprehensive skin and recovery support”",
        body: "GLOW and KLOW Multi-Peptide Blends combine recovery compounds with skin-supporting GHK-Cu (and, for KLOW, additional anti-inflammatory action). For members wanting broader-spectrum support than single-compound approaches.",
        whereLabel: "Recovery & Healing program (GLOW, KLOW)",
        to: "/programs/recovery-healing",
      },
    ],
  },
  {
    n: "05",
    title: "Energy, vitality, and body composition",
    concerns: [
      {
        h: "“My energy isn’t what it used to be”",
        body: "“Low energy” is one of the most common concerns and one of the hardest to address because it has so many possible causes. Sleep quality, hormonal shifts, metabolic factors, chronic stress, weight, mood, and underlying medical issues all contribute. Sometimes the answer is addressing the upstream cause through Weight Management or Recovery; sometimes it’s hormonal support through our upcoming Vitality & Performance program.",
        whereLabel: "Assessment-led consultation ($75) to identify what’s driving it, or Vitality & Performance program (coming soon)",
        to: "/get-access",
      },
      {
        h: "“I want to address body composition — specifically visceral fat”",
        body: "Tesamorelin (a GHRH analog) has FDA-approved use for visceral fat reduction in specific clinical contexts. For members where visceral/belly fat is the primary concern beyond general weight, this is a more targeted approach than GLP-1 family compounds.",
        whereLabel: "Vitality & Performance program (coming soon — Tesamorelin)",
        to: "/programs/vitality-performance",
      },
      {
        h: "“I want growth hormone support for recovery, sleep, and body composition”",
        body: "CJC-1295 + Ipamorelin blend supports your body’s own growth hormone production through complementary mechanisms (GHRH analog plus selective secretagogue). Used by members wanting body composition, recovery, sleep, and vitality support without external hormone replacement.",
        whereLabel: "Vitality & Performance program (coming soon)",
        to: "/programs/vitality-performance",
      },
    ],
  },
  {
    n: "06",
    title: "Cognitive function and mental performance",
    concerns: [
      {
        h: "“My focus fades through the day”",
        body: "Cognitive fatigue, difficulty sustaining attention, mental fog through the afternoon — these can have many causes. Sometimes they’re downstream of sleep issues, hormonal shifts, or metabolic factors. Sometimes they respond well to nootropic peptides like Semax. Our upcoming Cognitive Performance program addresses these concerns with a 7-compound lineup including established nootropics and investigational neuroprotectives.",
        whereLabel: "Cognitive Performance program (coming soon), or assessment-led consultation if you suspect underlying causes",
        to: "/programs/cognitive-performance",
      },
      {
        h: "“I’m worried about cognitive aging — memory, sharpness, neural protection”",
        body: "Neuroprotective peptides (Cerebrolysin, P21, ARA-290) and emerging research on neurogenesis support are part of our Cognitive Performance program in development. Our medical director is finalizing protocols carefully because cognitive concerns are clinically complex.",
        whereLabel: "Cognitive Performance program (coming soon)",
        to: "/programs/cognitive-performance",
      },
      {
        h: "“Stress and anxiety are affecting my cognitive function”",
        body: "Selank (an anxiolytic peptide) and DSIP (for sleep quality affecting cognition) address the stress and sleep dimensions of cognitive performance. These are part of our Cognitive Performance program in development.",
        whereLabel: "Cognitive Performance program (coming soon)",
        to: "/programs/cognitive-performance",
      },
    ],
  },
  {
    n: "07",
    title: "Longevity and cellular health",
    concerns: [
      {
        h: "“I want to invest in long-term cellular health and aging well”",
        body: "Mitochondrial peptides (MOTS-c, SS-31, Humanin), NAD+ precursors, telomerase support (Epithalon), and antioxidant protocols (Glutathione) are part of our Longevity & Cellular Health program in development. Substantial 7-compound lineup with combination protocols for members serious about long-term health investment.",
        whereLabel: "Longevity & Cellular Health program (coming soon)",
        to: "/programs/longevity-cellular-health",
      },
      {
        h: "“Mitochondrial function — energy at the cellular level”",
        body: "MOTS-c and SS-31 (Elamipretide) target mitochondrial function specifically. For members where cellular energy is the priority — fatigue, metabolic dysfunction, aging concerns — these compounds may fit.",
        whereLabel: "Longevity & Cellular Health program (coming soon)",
        to: "/programs/longevity-cellular-health",
      },
      {
        h: "“Immune support and resilience as I age”",
        body: "Thymosin Alpha-1 supports immune function through thymic peptide modulation. Particularly relevant for members with immune-related concerns or those wanting comprehensive longevity protocols.",
        whereLabel: "Longevity & Cellular Health program (coming soon)",
        to: "/programs/longevity-cellular-health",
      },
    ],
  },
];

// "When VOYA9 isn't the answer" — intentionally prominent. Each item is
// a brief paragraph; the typography keeps them readable and unminimized.
const NOT_THE_ANSWER = [
  {
    h: "Acute medical emergencies.",
    body: "Chest pain, sudden severe headache, signs of stroke, severe abdominal pain, anything that could be an emergency — call 911 or go to the emergency room. We’re not equipped for acute care.",
  },
  {
    h: "Mental health crises.",
    body: "Suicidal thoughts, severe depression or anxiety, addiction issues, eating disorders. These need specialized mental health professionals and may need urgent intervention. Call your physician, a crisis line (988 in the US), or your local mental health resource.",
  },
  {
    h: "Conditions needing imaging or specialist evaluation.",
    body: "A new lump. Persistent unexplained pain. Significant unexplained symptoms. These need primary care evaluation before peptide therapy is appropriate. We’ll often send you back to your primary care or to a specialist before we’ll prescribe.",
  },
  {
    h: "Pregnancy planning, fertility concerns, or active pregnancy.",
    body: "Most of our therapies are not recommended during pregnancy or while trying to conceive. Reproductive endocrinology and OB/GYN are the right starting points.",
  },
  {
    h: "Children and adolescents.",
    body: "VOYA9 serves adults only. Pediatric concerns belong with pediatricians.",
  },
  {
    h: "Hormone replacement therapy (testosterone, estrogen).",
    body: "We support growth hormone production and other peptide mechanisms, but we don’t provide hormone replacement therapy in the traditional sense. Members specifically wanting TRT or HRT should work with providers specialized in those services.",
  },
  {
    h: "Substantial psychiatric medication interactions.",
    body: "Some of our therapies have meaningful interactions with psychiatric medications. We’ll assess these carefully during consultation, and sometimes the right answer is “your psychiatrist needs to be your primary partner in this, not us.”",
  },
  {
    h: "Drug-tested competitive athletes.",
    body: "Many compounds we offer (TB-500 since 2009, BPC-157 since 2022, KPV, others) are on the WADA Prohibited List. If you compete at a level subject to drug testing, work with your team physician and us together — sometimes we have compounds you can use (GHK-Cu, PT-141), sometimes we don’t.",
  },
];

const BRAND_DISCIPLINE = [
  {
    h: "We’re not a maximum-everything platform.",
    body: "Members seeking the strongest doses of the highest-efficacy compounds regardless of clinical fit aren’t our right audience. Our physicians don’t escalate beyond what’s clinically appropriate. This protects members but means we sometimes disappoint people who came for aggressive prescribing.",
  },
  {
    h: "We’re not a peptide vending machine.",
    body: "Members who join, decline physician recommendations, switch providers, return, decline again aren’t our right fit either. We’re a clinical service, which means clinical evaluation is part of the process.",
  },
  {
    h: "Honest pricing.",
    body: "Our consult fees ($49 choice-led, $75 assessment-led) reflect the work of physician evaluation. Our compound pricing reflects the supply costs and the value of physician-managed protocols. We’re not the cheapest option in this category, and we don’t try to be.",
  },
  {
    h: "We say no when we should.",
    body: "Probably 10–15% of members who complete consultations get redirected — to other providers, to primary care, to specialist evaluation, or to “this isn’t the right fit for you” honesty. That’s a feature of good care, not a failure of conversion.",
  },
];

const ACTIVE_PROGRAMS = [
  { label: "Weight Management →", to: "/programs/weight-management" },
  { label: "Recovery & Healing →", to: "/programs/recovery-healing" },
  { label: "Sexual Wellness →", to: "/programs/sexual-wellness" },
];

const COMING_SOON = [
  { label: "Cognitive Performance →", to: "/programs/cognitive-performance" },
  { label: "Longevity & Cellular Health →", to: "/programs/longevity-cellular-health" },
  { label: "Vitality & Performance →", to: "/programs/vitality-performance" },
];

export default function WhatWeHelpWith() {
  useMarketingSEO({
    title: "What We Help With — VOYA9 Concerns and Programs",
    description: "The concerns members bring to VOYA9 — weight, recovery, sexual wellness, and more — and the physician-supervised programs that address them.",
    canonicalUrl: "https://voya9.com/what-we-help-with",
  });

  return (
    <div data-testid="what-we-help-with-page" style={{ background: C.cream, minHeight: "100vh" }}>
      <PublicNav variant="dark" />
      <WhatWeHelpWithBody />
      <Footer prevColor={C.cream} />
    </div>
  );
}

// Same content rendered without PublicNav/Footer chrome — used by the
// in-app route (/app/what-we-help-with) where AppShell provides the
// surrounding member-area navigation. Keeps members from being kicked
// out to the marketing chrome (and feeling logged out) when they tap
// the "What we help with" link from Browse.
export function WhatWeHelpWithInApp() {
  useMarketingSEO({
    title: "What We Help With — VOYA9 Concerns and Programs",
    description: "The concerns members bring to VOYA9 — weight, recovery, sexual wellness, and more — and the physician-supervised programs that address them.",
    canonicalUrl: "https://voya9.com/what-we-help-with",
  });
  return (
    <div data-testid="what-we-help-with-page" style={{ background: C.cream, minHeight: "100vh", paddingBottom: 120 }}>
      <WhatWeHelpWithBody inApp />
    </div>
  );
}

function WhatWeHelpWithBody({ inApp = false }) {
  // When rendered inside AppShell, link out to the in-app program pages
  // (/app/programs/<slug>) so members never leave the authenticated
  // shell. Public visitors keep getting the marketing surfaces.
  const linkBase = inApp ? "/app/programs" : "/programs";
  const remap = (to) => to.startsWith("/programs/") ? to.replace("/programs", linkBase) : to;
  const ASSESSMENT_HREF = inApp ? "/app" : "/get-access";

  return (
    <>
      {/* Header */}
      <header
        style={{
          background: C.ink,
          color: C.cream,
          padding: "96px 24px 112px",
          // Diagonal cut to match other ink heroes
          clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 32px), 0 100%)",
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ FINDING YOUR FIT</Eyebrow>
          <h1 style={{ marginTop: 24 }}>
            <Headline
              before="WHAT WE"
              italic="help with."
              color={C.cream}
              italicColor={C.peach}
              size={84}
            />
          </h1>
          <div style={{ marginTop: 36, display: "flex", flexDirection: "column", gap: 18, maxWidth: 720 }}>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: "rgba(250,246,240,0.82)", margin: 0 }}>
              People come to VOYA9 for different reasons. Some know exactly what they want — they’ve researched semaglutide, they’re ready to start. Others arrive with concerns but uncertain about whether peptide therapy is right for them. Most are somewhere in between.
            </p>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: "rgba(250,246,240,0.82)", margin: 0 }}>
              This page catalogs the concerns we work with and the programs that address them. If you find yourself on this page, you’re probably trying to figure out whether VOYA9 is for you. We’ll be honest about what we offer, what we don’t, and when other paths might serve you better.
            </p>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: "rgba(250,246,240,0.82)", margin: 0 }}>
              We’re a physician-led wellness platform offering peptide therapeutics through licensed physician consultations. We’re not a substitute for primary care, mental health treatment, or specialist medical care. We work alongside those things.
            </p>
          </div>
        </div>
      </header>

      {/* How to use this page */}
      <section style={{ padding: "56px 24px 12px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", background: C.bone, padding: "28px 28px", borderLeft: `3px solid ${C.peachDeep}` }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
            ★ HOW TO USE THIS PAGE
          </div>
          <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.7, color: C.ink, margin: "14px 0 0" }}>
            Scan the concerns below. When you see something that resonates, follow the link to the relevant program. If you’re not sure where you fit, the assessment-led consultation ($75) puts your situation in front of a physician who recommends the right starting point.
          </p>
          <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.7, color: C.ink, margin: "12px 0 0" }}>
            If you don’t see your concern here, we may not be the right fit. We’ll be explicit about that at the bottom.
          </p>
        </div>
      </section>

      {/* Concern categories */}
      <section data-testid="concern-categories" style={{ padding: "32px 24px 24px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto", display: "flex", flexDirection: "column", gap: 56 }}>
          {CATEGORIES.map((cat) => (
            <ConcernCategory key={cat.n} category={cat} remap={remap} assessmentHref={ASSESSMENT_HREF} />
          ))}
        </div>
      </section>

      {/* "When you're not sure" — bridge into the assessment-led path */}
      <section data-testid="not-sure-section" style={{ padding: "40px 24px 32px" }}>
        <div
          style={{
            maxWidth: 920,
            margin: "0 auto",
            background: C.peach,
            color: C.peachDark,
            padding: "44px 36px",
          }}
        >
          <Eyebrow color={C.peachDark}>★ WHEN YOU’RE NOT SURE WHERE YOU FIT</Eyebrow>
          <h2 style={{ marginTop: 14 }}>
            <Headline
              before="LET A PHYSICIAN"
              italic="map your path."
              color={C.peachDark}
              italicColor={C.peachDark}
              size={44}
            />
          </h2>
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.7, color: C.peachDark, margin: "22px 0 0", maxWidth: 720 }}>
            Some concerns don’t map neatly to one program. Some members have multiple overlapping goals. Some arrive thinking they want one thing but a physician’s evaluation suggests something else.
          </p>
          <p style={{ marginTop: 12, fontSize: 16, lineHeight: 1.7, color: C.peachDark, margin: "12px 0 0", maxWidth: 720 }}>
            For complex or unclear situations, the <strong>assessment-led consultation ($75)</strong> is the right path. You complete the full health assessment without picking a program. Your physician reviews your complete clinical picture and recommends the program — or combination of programs — most likely to fit your situation. This is especially appropriate when:
          </p>
          <ul style={{ marginTop: 14, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8, maxWidth: 720 }}>
            {[
              "You have multiple concerns spanning different programs",
              "You’re not sure whether your concern is upstream of another (e.g., is your fatigue from weight, sleep, hormones, mood, or something else?)",
              "You’ve tried other approaches and want a fresh clinical evaluation",
              "You don’t have strong preferences and want physician guidance from the start",
            ].map((t, i) => (
              <li key={i} style={{ paddingLeft: 20, position: "relative", fontSize: 15, lineHeight: 1.6, color: C.peachDark }}>
                <span style={{ position: "absolute", left: 0, color: C.peachDark, fontWeight: 800 }}>•</span>
                {t}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 28 }}>
            <Link to={ASSESSMENT_HREF} data-testid="what-we-help-with-assessment-cta" style={{ textDecoration: "none" }}>
              <Button variant="primary" size="lg">
                {inApp ? "GO TO MY DASHBOARD →" : "TAKE THE ASSESSMENT-LED CONSULTATION →"}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* "When VOYA9 isn't the answer" — prominent boundary section */}
      <section data-testid="not-the-answer-section" style={{ padding: "48px 24px 24px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <Eyebrow color={C.coral}>★ WHEN VOYA9 ISN’T THE ANSWER</Eyebrow>
          <h2 style={{ marginTop: 14 }}>
            <Headline
              before="HONEST BOUNDARIES,"
              italic="not minimized."
              color={C.ink}
              italicColor={C.coral}
              size={44}
            />
          </h2>
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.7, color: C.ink, margin: "22px 0 0", maxWidth: 720 }}>
            Being explicit about what we don’t do is part of doing this honestly. The following concerns are not within our scope, and we’ll often redirect you to more appropriate care:
          </p>
          <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
            {NOT_THE_ANSWER.map((item, i) => (
              <div
                key={i}
                style={{
                  background: C.bone,
                  padding: "20px 22px",
                  borderLeft: `3px solid ${C.coral}`,
                }}
              >
                <h3 style={{ fontSize: 16, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: "-0.005em" }}>
                  {item.h}
                </h3>
                <p style={{ marginTop: 8, fontSize: 14.5, lineHeight: 1.65, color: C.ink, margin: "8px 0 0" }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand-discipline observations — subtle but visible */}
      <section data-testid="brand-discipline-section" style={{ padding: "40px 24px 24px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <Eyebrow color={C.warmGray}>★ BRAND-DISCIPLINE OBSERVATIONS</Eyebrow>
          <h2 style={{ marginTop: 14 }}>
            <Headline
              before="A FEW HONEST"
              italic="framings."
              color={C.ink}
              italicColor={C.warmGray}
              size={42}
            />
          </h2>
          <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.7, color: C.warmGray, margin: "18px 0 0", maxWidth: 720 }}>
            A few honest framings worth including, because they shape who we’re right for:
          </p>
          <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 22, maxWidth: 760 }}>
            {BRAND_DISCIPLINE.map((item, i) => (
              <div key={i}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: "-0.005em" }}>
                  {item.h}
                </h3>
                <p style={{ marginTop: 6, fontSize: 15, lineHeight: 1.65, color: C.ink, margin: "6px 0 0" }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom navigation — find your starting point */}
      <section data-testid="bottom-nav-section" style={{ padding: "56px 24px 96px" }}>
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <Eyebrow color={C.peachDeep}>★ HOW TO FIND YOUR STARTING POINT</Eyebrow>
          <h2 style={{ marginTop: 14 }}>
            <Headline
              before="PICK A"
              italic="thread to pull."
              color={C.ink}
              italicColor={C.peachDeep}
              size={44}
            />
          </h2>
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.7, color: C.ink, margin: "22px 0 0", maxWidth: 720 }}>
            If you’ve read this far and one concern resonated more than others, click through to the relevant program. Each program page has detailed information about compounds, what to expect, and how to start. If multiple concerns resonated, the assessment-led consultation ($75) is where your physician helps you sequence your approach.
          </p>

          <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "1fr", gap: 20 }} className="!grid-cols-1 md:!grid-cols-3">
            <BottomCol heading="ACTIVE PROGRAMS" links={ACTIVE_PROGRAMS.map((l) => ({ ...l, to: remap(l.to) }))} accent={C.peachDeep} testId="bottom-active" />
            <BottomCol heading="COMING SOON" links={COMING_SOON.map((l) => ({ ...l, to: remap(l.to) }))} accent={C.warmGray} testId="bottom-coming-soon" />
            <BottomCol
              heading="NOT SURE?"
              links={[{ label: inApp ? "Go to my dashboard →" : "Assessment-led consultation ($75) →", to: ASSESSMENT_HREF }]}
              accent={C.peach}
              testId="bottom-not-sure"
            />
          </div>
        </div>
      </section>
    </>
  );
}

// ---- helpers ----

function ConcernCategory({ category, remap }) {
  return (
    <section data-testid={`concern-category-${category.n}`}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
        ★ CATEGORY {category.n}
      </div>
      <h2
        style={{
          marginTop: 10,
          fontSize: 32,
          fontWeight: 800,
          color: C.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1.15,
        }}
      >
        {category.title}
      </h2>
      <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 22 }}>
        {category.concerns.map((c, i) => (
          <ConcernEntry key={i} concern={c} remap={remap} />
        ))}
      </div>
    </section>
  );
}

function ConcernEntry({ concern, remap }) {
  const href = remap ? remap(concern.to) : concern.to;
  return (
    <article
      style={{
        background: C.pure,
        padding: "22px 24px",
        border: `1px solid ${C.warmGray3}`,
      }}
    >
      <h3
        style={{
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          fontSize: 22,
          color: C.ink,
          margin: 0,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
        }}
      >
        {concern.h}
      </h3>
      <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.7, color: C.ink, margin: "12px 0 0" }}>
        {concern.body}
      </p>
      <Link
        to={href}
        data-testid={`concern-where-link-${concern.to.replace(/\//g, "-")}`}
        style={{
          marginTop: 14,
          display: "inline-block",
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.18em",
          color: C.peachDeep,
          textDecoration: "none",
          borderBottom: `2px solid ${C.peachDeep}`,
          paddingBottom: 3,
          textTransform: "uppercase",
        }}
      >
        WHERE THIS GOES: {concern.whereLabel} →
      </Link>
    </article>
  );
}

function BottomCol({ heading, links, accent, testId }) {
  return (
    <div data-testid={testId} style={{ background: C.bone, padding: "22px 22px" }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: accent }}>
        ★ {heading}
      </div>
      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {links.map((l, i) => (
          <Link
            key={i}
            to={l.to}
            data-testid={`${testId}-link-${i}`}
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: C.ink,
              textDecoration: "none",
              borderBottom: `1px solid ${C.warmGray3}`,
              paddingBottom: 8,
            }}
          >
            {l.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
